import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { exportCsv, exportPdf, exportExcel } from "../lib/exporters";
import { downloadUserGuide } from "../lib/userGuide";
import { downloadWorkflowPdf, downloadWorkflowSvgZip } from "../lib/workflowDiagrams";
import { CURRENCIES, FX_SUPPORTED_CODES } from "../lib/s1000dData";
import { fmtHours } from "../lib/calculations";
import { toast } from "sonner";
import { ChevronDown, X, Plus } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import UserGuidePanel from "./UserGuidePanel";
import ISO_3166_COUNTRIES from "../../../data/iso3166Countries";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header({ activeTab, onTabChange }) {
  const { t, i18n } = useTranslation();
  const {
    estimate, totals, combined, resourcing, setProjectName, setNotes, setCountry, setCurrency, saveEstimate, resetEstimate,
    duplicateEstimate, formatCurrency, addTag, removeTag,
  } = useEstimate();
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const handleSave = async () => {
    if (!estimate.projectName?.trim()) {
      toast.error("Please enter a project name before saving.");
      return;
    }
    setSaving(true);
    try {
      await saveEstimate();
      toast.success("Estimate saved");
    } catch {
      toast.error("Failed to save estimate");
    } finally {
      setSaving(false);
      setActionsOpen(false);
    }
  };

  const handleShare = async () => {
    if (!estimate.id) {
      toast.error("Save the estimate first to generate a share link.");
      return;
    }
    const url = `${window.location.origin}/share/${estimate.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to clipboard", { description: url });
    } catch {
      window.prompt("Copy this read-only share link:", url);
    }
    setActionsOpen(false);
  };

  const handleDuplicate = () => {
    duplicateEstimate();
    toast.message("Duplicated", { description: "Edit and click Save to persist as a new estimate." });
    setActionsOpen(false);
  };

  const handleCreateProject = async () => {
    const name = estimate.projectName?.trim();
    if (!name) {
      toast.error("Please enter a project name before creating a project.");
      return;
    }
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("You must be logged in to create a project.");
      return;
    }
    setCreating(true);
    try {
      const issueNum = estimate.brexPlan?.s1000dIssue || "5.0";
      const s1000dVersion = `Issue ${issueNum}`;
      const currencyCode = estimate.currency?.code || "AUD";
      const totalBudget = combined.combinedGrandTotal || 0;
      const targetDms = totals.totalDms || 0;
      const roleBreakdown = totals.roleBreakdown || [];
      const rolesRates = (estimate.roles || []).map((r, i) => ({
        name: r.name || "",
        cost: roleBreakdown[i]?.roleCost || 0,
      }));
      const today = new Date();
      const startDate = today.toISOString().split("T")[0];
      let endDate = "";
      const workingDays = resourcing?.workingDays || 0;
      if (workingDays > 0) {
        const calendarDays = Math.ceil(workingDays * 7 / 5);
        const end = new Date(today);
        end.setDate(end.getDate() + calendarDays);
        endDate = end.toISOString().split("T")[0];
      } else if (totals.totalHours > 0) {
        const effHrsPerDay = estimate.resourcePlan?.effectiveHoursPerDay || 6;
        const estWorkingDays = Math.ceil(totals.totalHours / effHrsPerDay);
        const calendarDays = Math.ceil(estWorkingDays * 7 / 5);
        const end = new Date(today);
        end.setDate(end.getDate() + calendarDays);
        endDate = end.toISOString().split("T")[0];
      }
      const languageCode = (i18n.language || "en").split("-")[0].toLowerCase();
      const payload = {
        title: name,
        description: estimate.notes || "",
        language_iso_code: languageCode,
        country_iso_code: estimate.country || "AU",
        s1000d_version: s1000dVersion,
        analytics: { start_date: startDate, end_date: endDate, currency: currencyCode, total_budget: totalBudget, target_total_dms: targetDms },
        roles_rates: rolesRates,
      };
      const res = await fetch(`${BACKEND_URL}/api/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (${res.status})`);
      }
      toast.success(`Project "${name}" created`, {
        description: `S1000D ${s1000dVersion} · ${currencyCode} ${formatCurrency(totalBudget)} · ${targetDms} DMs · ${rolesRates.length} roles`,
      });
    } catch (e) {
      toast.error("Failed to create project", { description: e.message });
    } finally {
      setCreating(false);
      setActionsOpen(false);
    }
  };

  const handleWorkflowPdf = () => {
    try { downloadWorkflowPdf(); toast.success("Workflow diagram PDF downloaded"); }
    catch { toast.error("Failed to generate workflow PDF"); }
    setActionsOpen(false);
  };

  const handleWorkflowSvgZip = async () => {
    try { await downloadWorkflowSvgZip(); toast.success("Workflow SVG pack downloaded"); }
    catch { toast.error("Failed to generate workflow SVG pack"); }
    setActionsOpen(false);
  };

  const handleTagKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const v = tagInput.trim().replace(/,$/, "");
      if (v) addTag(v);
      setTagInput("");
    } else if (e.key === "Backspace" && !tagInput && estimate.tags.length > 0) {
      removeTag(estimate.tags[estimate.tags.length - 1]);
    }
  };

  const tabGroups = [
    {
      key: "plan",
      label: t("tabGroups.plan"),
      tabs: [
        { key: "roles", label: t("tabs.roles") },
        { key: "ratecards", label: t("tabs.ratecards") },
        { key: "resourcing", label: t("tabs.resourcing") },
        { key: "schedule", label: t("tabs.schedule") },
      ],
    },
    {
      key: "scope",
      label: t("tabGroups.scope"),
      tabs: [
        { key: "sbs", label: t("tabs.sbs") },
        { key: "modules", label: t("tabs.modules") },
        { key: "illustrations", label: t("tabs.illustrations") },
        { key: "reviews", label: "Review & Update" },
        { key: "dml", label: t("tabs.dml") },
        { key: "brex", label: t("tabs.brex") },
      ],
    },
    {
      key: "bid",
      label: t("tabGroups.bid"),
      tabs: [
        { key: "risk", label: t("tabs.risk") },
        { key: "bidprice", label: t("tabs.bidprice") },
        { key: "summary", label: t("tabs.summary") },
        { key: "readiness", label: t("tabs.readiness") },
      ],
    },
    {
      key: "library",
      label: t("tabGroups.library"),
      tabs: [
        { key: "compare", label: t("tabs.compare") },
        { key: "portfolio", label: t("tabs.portfolio") },
        { key: "projects", label: t("tabs.projects") },
      ],
    },
  ];

  const activeGroup = tabGroups.find((g) => g.tabs.some((tb) => tb.key === activeTab)) || tabGroups[0];
  const activeGroupTabs = activeGroup.tabs;
  const code = estimate.currency?.code || "AUD";
  const fxLive = FX_SUPPORTED_CODES.includes(code);

  return (
    <>
      {/* ── Header card: icon + title left, stats right ── */}
      <div
        className="flex items-center justify-between gap-6 bg-white border border-[#E7E6E2] rounded-[14px] p-5 px-6 mb-4 flex-wrap"
        data-testid="estimator-header-card"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6D3FE0] to-[#26215C] flex items-center justify-center flex-none">
            <img src="/assets/favicon-icon.png" alt="MySpecWorkspace" className="w-9 h-9 object-contain" />
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#8A887F]">
              {t("header.appSubtitle")}
            </p>
            <h1 className="text-[22px] font-extrabold tracking-tight text-[#111] leading-none mt-0.5">
              {t("header.appTitle")}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-7 flex-wrap" data-testid="header-stats">
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.05em] text-[#8A887F] font-bold">{combined.mergeBrex ? "Hours (DM+BREX)" : "Total hours"}</div>
            <div className="text-lg font-extrabold text-[#111] mt-0.5" data-testid="estimator-footer-total-hours">{fmtHours(combined.combinedHours)}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.05em] text-[#8A887F] font-bold">Blended rate</div>
            <div className="text-lg font-extrabold text-[#111] mt-0.5" data-testid="estimator-footer-blended-rate">{formatCurrency(totals.blendedRate)}/hr</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.05em] text-[#8A887F] font-bold">Modules / illus</div>
            <div className="text-lg font-extrabold text-[#111] mt-0.5" data-testid="estimator-footer-modules">{totals.totalDms} / {totals.totalIllus}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.05em] text-[#8A887F] font-bold">{combined.mergeBrex ? "Total project cost · incl. BREX" : "Total project cost"}</div>
            <div className="text-[22px] font-extrabold text-[#5A2FC7] mt-0.5" data-testid="total-cost-badge">{formatCurrency(combined.combinedGrandTotal)}</div>
          </div>
        </div>
      </div>

      {/* ── Form row: 3-col grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1.4fr] gap-4 mb-4">
        {/* Project field */}
        <div className="bg-white border border-[#E7E6E2] rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A887F]">{t("header.project")}</label>
            {/* Actions dropdown */}
            <div className="relative" ref={actionsRef}>
              <button
                data-testid="actions-dropdown-btn"
                onClick={(e) => { e.stopPropagation(); setActionsOpen((v) => !v); }}
                className="bg-[#6D3FE0] hover:bg-[#5A2FC7] text-white text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors"
              >
                Actions <ChevronDown size={12} className={actionsOpen ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {actionsOpen && (
                <div className="absolute right-0 top-[calc(100%+6px)] bg-white border border-[#E7E6E2] rounded-[10px] shadow-lg z-30 min-w-[170px] p-1.5" data-testid="actions-dropdown-menu">
                  <button data-testid="new-estimate-btn" onClick={() => { resetEstimate(); toast.message(t("actions.new")); setActionsOpen(false); }} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.new")}</button>
                  <button data-testid="duplicate-estimate-btn" onClick={handleDuplicate} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.duplicate")}</button>
                  <button data-testid="save-estimate-btn" onClick={handleSave} disabled={saving} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1] disabled:opacity-50">{saving ? t("actions.saving") : t("actions.save")}</button>
                  <button data-testid="create-project-btn" onClick={handleCreateProject} disabled={creating} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1] text-[#5A2FC7] disabled:opacity-50">{creating ? "Creating…" : "Create project"}</button>
                  <div className="h-px bg-[#E7E6E2] my-1 mx-0.5" />
                  <button data-testid="share-link-btn" onClick={handleShare} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.share")}</button>
                  <button data-testid="user-guide-btn" onClick={() => { setHelpOpen(true); setActionsOpen(false); }} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.guide")}</button>
                  <button data-testid="workflow-pdf-btn" onClick={handleWorkflowPdf} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">Workflow</button>
                  <div className="h-px bg-[#E7E6E2] my-1 mx-0.5" />
                  <button data-testid="export-csv-btn" onClick={() => { exportCsv(estimate); setActionsOpen(false); }} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.csv")}</button>
                  <button data-testid="export-xlsx-btn" onClick={() => { exportExcel(estimate); setActionsOpen(false); }} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.xlsx")}</button>
                  <button data-testid="export-pdf-btn" onClick={() => { exportPdf(estimate); setActionsOpen(false); }} className="w-full text-left px-2.5 py-2 text-[13px] font-semibold rounded-[7px] hover:bg-[#F3F3F1]">{t("actions.pdf")}</button>
                </div>
              )}
            </div>
          </div>
          <input
            data-testid="project-name-input"
            value={estimate.projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder={t("header.projectPlaceholder")}
            className="w-full border border-[#E7E6E2] rounded-lg px-2.5 py-2 text-sm bg-[#FAFAFA] text-[#111] placeholder:text-[#B4B2A9] focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
          />
          {estimate.id && (
            <span data-testid="estimate-id-badge" className="inline-block mt-2 font-mono text-[10px] text-[#8A887F] border border-[#E7E6E2] px-2 py-1 rounded">
              ID {estimate.id.slice(0, 8)}
            </span>
          )}
          <div className="mt-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.05em] text-[#8A887F] mb-1.5">Description</label>
            <textarea
              data-testid="project-description-input"
              value={estimate.notes || ""}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a short description of this project…"
              rows="3"
              className="w-full border border-[#E7E6E2] rounded-lg px-2.5 py-2 text-[13px] bg-[#FAFAFA] text-[#111] placeholder:text-[#B4B2A9] resize-y focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
            />
          </div>
        </div>

        {/* Currency field */}
        <div className="bg-white border border-[#E7E6E2] rounded-xl p-4">
          <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A887F] mb-2">{t("header.currency")}</label>
          <select
            data-testid="currency-select"
            value={code}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full border border-[#E7E6E2] rounded-lg px-2.5 py-2 text-sm bg-[#FAFAFA] text-[#111] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
          >
            <optgroup label={t("header.currencyGroupFx")}>
              {CURRENCIES.filter((c) => c.fxSupported).map((c) => (
                <option key={c.code} value={c.code}>{`${c.symbol} ${c.code} · ${c.label}`}</option>
              ))}
            </optgroup>
            <optgroup label={t("header.currencyGroupOther")}>
              {CURRENCIES.filter((c) => !c.fxSupported).map((c) => (
                <option key={c.code} value={c.code}>{`${c.symbol} ${c.code} · ${c.label}`}</option>
              ))}
            </optgroup>
          </select>
          <div
            data-testid="fx-pill"
            data-fx-live={fxLive ? "true" : "false"}
            title={fxLive ? t("header.fxLiveTitle") : t("header.fxNoneTitle")}
            className={
              "inline-flex items-center gap-1.5 mt-2 text-[11px] font-bold px-2.5 py-1 rounded-full " +
              (fxLive ? "bg-[#EAF6EE] text-[#1F8A4C]" : "bg-[#F3F3F1] text-[#5F5E5A]")
            }
          >
            <span className={"w-1.5 h-1.5 rounded-full " + (fxLive ? "bg-[#1F8A4C]" : "bg-[#8A887F]")} />
            {fxLive ? t("header.fxLive") : t("header.fxNone")}
          </div>
          <div className="mt-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.05em] text-[#8A887F] mb-1.5">Language</label>
            <LanguageSwitcher compact />
          </div>
          <div className="mt-3">
            <label className="block text-[10px] font-bold uppercase tracking-[0.05em] text-[#8A887F] mb-1.5">Country</label>
            <select
              data-testid="country-select"
              value={estimate.country || "AU"}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full border border-[#E7E6E2] rounded-lg px-2.5 py-2 text-sm bg-[#FAFAFA] text-[#111] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
            >
              {ISO_3166_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tags field */}
        <div className="bg-white border border-[#E7E6E2] rounded-xl p-4" data-testid="tags-row">
          <label className="block text-[11px] font-bold uppercase tracking-[0.05em] text-[#8A887F] mb-2">{t("header.tags")}</label>
          <div className="flex items-center gap-2">
            <input
              data-testid="tag-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKey}
              placeholder={t("header.tagsPlaceholder")}
              className="flex-1 border border-[#E7E6E2] rounded-lg px-2.5 py-2 text-sm bg-[#FAFAFA] text-[#111] placeholder:text-[#B4B2A9] focus:outline-none focus:ring-2 focus:ring-[#6D3FE0]"
            />
            <button
              data-testid="add-tag-btn"
              onClick={() => { const v = tagInput.trim(); if (v) addTag(v); setTagInput(""); }}
              className="flex-none bg-[#6D3FE0] hover:bg-[#5A2FC7] text-white text-[13px] font-bold px-3.5 py-2 rounded-lg transition-colors"
            >
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2.5">
            {estimate.tags.map((tag) => (
              <span
                key={tag}
                data-testid={`tag-chip-${tag}`}
                className="inline-flex items-center gap-1.5 bg-[#F4F2FE] text-[#5A2FC7] text-xs font-bold px-2.5 py-1 rounded-full"
              >
                {tag}
                <button onClick={() => removeTag(tag)} className="text-[#7F77DD] hover:text-[#5A2FC7]" aria-label={`Remove tag ${tag}`}>
                  <X size={12} strokeWidth={2.5} />
                </button>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ── Tier-1: group tabs (pill-style) ── */}
      <div className="flex gap-1.5 bg-white border border-[#E7E6E2] rounded-[10px] p-[5px] w-fit mb-0" data-testid="main-groups">
        {tabGroups.map((g) => {
          const isActive = activeGroup.key === g.key;
          return (
            <button
              key={g.key}
              data-testid={`group-${g.key}`}
              onClick={() => onTabChange(g.tabs[0].key)}
              className={
                "px-5 py-2 rounded-[7px] text-[13px] font-bold cursor-pointer transition-colors " +
                (isActive ? "bg-[#111] text-white" : "text-[#5F5E5A] hover:bg-[#F3F3F1]")
              }
            >
              {g.label}
            </button>
          );
        })}
      </div>

      {/* ── Tier-2: subtabs (underline-style) ── */}
      <div className="flex gap-6 border-b border-[#E7E6E2] mt-4 mb-5 text-[13px] font-bold text-[#8A887F]" data-testid="main-tabs">
        {activeGroupTabs.map((tab) => (
          <button
            key={tab.key}
            data-testid={`tab-${tab.key}`}
            onClick={() => onTabChange(tab.key)}
            className={
              "pb-3 cursor-pointer transition-colors whitespace-nowrap border-b-2 " +
              (activeTab === tab.key
                ? "text-[#111] border-[#111]"
                : "border-transparent hover:text-[#111]")
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      <UserGuidePanel open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  );
}
